import { c as _c } from "react/compiler-runtime";
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BookOpenIcon from '@/assets/icons/heroIcons/Book-open.svg';
import BriefcaseIcon from '@/assets/icons/heroIcons/Briefcase.svg';
import ChatIcon from '@/assets/icons/heroIcons/Chat.svg';
import ClipboardIcon from '@/assets/icons/heroIcons/Clipboard.svg';
import DesktopComputerIcon from '@/assets/icons/heroIcons/Desktop-computer.svg';
import NewspaperIcon from '@/assets/icons/heroIcons/Newspaper.svg';
import PhoneIcon from '@/assets/icons/heroIcons/Phone.svg';
import SaleIcon from '@/assets/icons/heroIcons/Sale.svg';
import ShoppingCartIcon from '@/assets/icons/heroIcons/Shopping-cart.svg';
import SpeakerphoneIcon from '@/assets/icons/heroIcons/Speakerphone.svg';
import TemplateIcon from '@/assets/icons/heroIcons/Template.svg';
import HandshakeIcon from '@/assets/icons/heroIcons/Handshake.svg';
import ThirdIcon from '@/assets/icons/heroIcons/Third.svg';
import CardFrontIcon from '@/assets/icons/heroIcons/Card-front.svg';
import DeferredPaymentIcon from '@/assets/icons/heroIcons/Deferred-payment.svg';
import LoanIcon from '@/assets/icons/heroIcons/Loan.svg';
import PayIcon from '@/assets/icons/heroIcons/Pay.svg';
import UserGroupIcon from '@/assets/icons/heroIcons/User-group.svg';
import MobileAppIcon from '@/assets/icons/heroIcons/Device-mobile.svg';
import ViewGridIcon from '@/assets/icons/heroIcons/View-grid.svg';
import { EXTERNAL_LINKS, LINKS } from '@/constants/links';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import { PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
type SectionBase = {
  id: 'products' | 'deferredPayment' | 'payInThree' | 'card' | 'loan' | 'revolving' | 'allProducts' | 'whereToShop' | 'promotionsAndDiscounts' | 'promotions' | 'discounts' | 'implementation' | 'forEshops' | 'forDevelopers' | 'aboutUs' | 'aboutCompany' | 'forMedia' | 'career' | 'blog' | 'other' | 'faq' | 'pricing' | 'contact' | 'mobileApp' | 'gcssLandingPage' | 'checkoutManual' | 'termsAndConditions';
  name: string;
  promo?: PageMenuPromoBanner;
};
export type SubSection = SectionBase & {
  description: string;
  icon: ReactNode;
  path: string;
};
export type Section = ({
  path: string;
} | {
  subsection: SubSection[];
}) & SectionBase;
export function useSections() {
  const $ = _c(522);
  const {
    t
  } = useTranslation();
  const isCsLanguage = useIsSelectedLanguage("cs");
  const isHuLanguage = useIsSelectedLanguage("hu");
  let t0;
  if ($[0] !== t) {
    t0 = t("products");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("products.deferredPayment.description");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DeferredPaymentIcon />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("products.deferredPayment");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = LINKS();
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t3) {
    t5 = {
      id: "deferredPayment",
      description: t1,
      icon: t2,
      name: t3,
      path: t4.deferredPayment
    };
    $[8] = t1;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("products.payInThree.description");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ThirdIcon />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("products.payInThree");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = LINKS();
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t8) {
    t10 = {
      id: "payInThree",
      description: t6,
      icon: t7,
      name: t8,
      path: t9.payInThree
    };
    $[17] = t6;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("products.card.description");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <CardFrontIcon />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t) {
    t13 = t("products.card");
    $[23] = t;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = LINKS();
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t13) {
    t15 = {
      id: "card",
      description: t11,
      icon: t12,
      name: t13,
      path: t14.card
    };
    $[26] = t11;
    $[27] = t13;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t) {
    t16 = t("products.loan.description");
    $[29] = t;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <LoanIcon />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t) {
    t18 = t("products.loan.short");
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = LINKS();
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t16 || $[36] !== t18) {
    t20 = {
      id: "loan",
      description: t16,
      icon: t17,
      name: t18,
      path: t19.loan
    };
    $[35] = t16;
    $[36] = t18;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t) {
    t21 = t("products.allProducts.description");
    $[38] = t;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ViewGridIcon />;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t) {
    t23 = t("products.allProducts");
    $[41] = t;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = LINKS();
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t21 || $[45] !== t23) {
    t25 = {
      id: "allProducts",
      description: t21,
      icon: t22,
      name: t23,
      path: t24.productsPackages
    };
    $[44] = t21;
    $[45] = t23;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t10 || $[48] !== t15 || $[49] !== t20 || $[50] !== t25 || $[51] !== t5) {
    t26 = [t5, t10, t15, t20, t25];
    $[47] = t10;
    $[48] = t15;
    $[49] = t20;
    $[50] = t25;
    $[51] = t5;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t0 || $[54] !== t26) {
    t27 = {
      id: "products",
      name: t0,
      subsection: t26
    };
    $[53] = t0;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t) {
    t28 = t("products.whereToShop");
    $[56] = t;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = LINKS();
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t28) {
    t30 = {
      id: "whereToShop",
      name: t28,
      path: t29.whereToShop
    };
    $[59] = t28;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t) {
    t31 = t("products.promotionsAndDiscounts");
    $[61] = t;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== t) {
    t32 = t("products.promotions.description");
    $[63] = t;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <SaleIcon />;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t) {
    t34 = t("products.promotions");
    $[66] = t;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = LINKS();
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t32 || $[70] !== t34) {
    t36 = {
      id: "promotions",
      description: t32,
      icon: t33,
      name: t34,
      path: t35.promotions
    };
    $[69] = t32;
    $[70] = t34;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== t) {
    t37 = t("products.discounts.description");
    $[72] = t;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <SpeakerphoneIcon />;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  let t39;
  if ($[75] !== t) {
    t39 = t("products.discounts");
    $[75] = t;
    $[76] = t39;
  } else {
    t39 = $[76];
  }
  let t40;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = EXTERNAL_LINKS();
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== t37 || $[79] !== t39) {
    t41 = {
      id: "discounts",
      description: t37,
      icon: t38,
      name: t39,
      path: t40.marketingPromos
    };
    $[78] = t37;
    $[79] = t39;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== t36 || $[82] !== t41) {
    t42 = [t36, t41];
    $[81] = t36;
    $[82] = t41;
    $[83] = t42;
  } else {
    t42 = $[83];
  }
  let t43;
  if ($[84] !== t31 || $[85] !== t42) {
    t43 = {
      id: "promotionsAndDiscounts",
      name: t31,
      subsection: t42
    };
    $[84] = t31;
    $[85] = t42;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t) {
    t44 = t("products.implementation");
    $[87] = t;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== t) {
    t45 = t("products.forEshops.description");
    $[89] = t;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <HandshakeIcon />;
    $[91] = t46;
  } else {
    t46 = $[91];
  }
  let t47;
  if ($[92] !== t) {
    t47 = t("products.forEshops");
    $[92] = t;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = LINKS();
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  let t49;
  if ($[95] !== t45 || $[96] !== t47) {
    t49 = {
      id: "forEshops",
      description: t45,
      icon: t46,
      name: t47,
      path: t48.forEshops
    };
    $[95] = t45;
    $[96] = t47;
    $[97] = t49;
  } else {
    t49 = $[97];
  }
  let t50;
  if ($[98] !== t) {
    t50 = t("products.forDevelopers.description");
    $[98] = t;
    $[99] = t50;
  } else {
    t50 = $[99];
  }
  let t51;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <DesktopComputerIcon />;
    $[100] = t51;
  } else {
    t51 = $[100];
  }
  let t52;
  if ($[101] !== t) {
    t52 = t("products.forDevelopers");
    $[101] = t;
    $[102] = t52;
  } else {
    t52 = $[102];
  }
  let t53;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = LINKS();
    $[103] = t53;
  } else {
    t53 = $[103];
  }
  let t54;
  if ($[104] !== t50 || $[105] !== t52) {
    t54 = {
      id: "forDevelopers",
      description: t50,
      icon: t51,
      name: t52,
      path: t53.forDevelopers
    };
    $[104] = t50;
    $[105] = t52;
    $[106] = t54;
  } else {
    t54 = $[106];
  }
  let t55;
  if ($[107] !== t) {
    t55 = t("products.gcss.description");
    $[107] = t;
    $[108] = t55;
  } else {
    t55 = $[108];
  }
  let t56;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <ShoppingCartIcon />;
    $[109] = t56;
  } else {
    t56 = $[109];
  }
  let t57;
  if ($[110] !== t) {
    t57 = t("products.gcss");
    $[110] = t;
    $[111] = t57;
  } else {
    t57 = $[111];
  }
  let t58;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = LINKS();
    $[112] = t58;
  } else {
    t58 = $[112];
  }
  let t59;
  if ($[113] !== t55 || $[114] !== t57) {
    t59 = {
      id: "gcssLandingPage",
      description: t55,
      icon: t56,
      name: t57,
      path: t58.gcssLandingPage
    };
    $[113] = t55;
    $[114] = t57;
    $[115] = t59;
  } else {
    t59 = $[115];
  }
  let t60;
  if ($[116] !== t) {
    t60 = t("products.checkoutManual.description");
    $[116] = t;
    $[117] = t60;
  } else {
    t60 = $[117];
  }
  let t61;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <TemplateIcon />;
    $[118] = t61;
  } else {
    t61 = $[118];
  }
  let t62;
  if ($[119] !== t) {
    t62 = t("products.checkoutManual");
    $[119] = t;
    $[120] = t62;
  } else {
    t62 = $[120];
  }
  let t63;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = EXTERNAL_LINKS();
    $[121] = t63;
  } else {
    t63 = $[121];
  }
  let t64;
  if ($[122] !== t60 || $[123] !== t62) {
    t64 = {
      id: "checkoutManual",
      description: t60,
      icon: t61,
      name: t62,
      path: t63.checkoutManual
    };
    $[122] = t60;
    $[123] = t62;
    $[124] = t64;
  } else {
    t64 = $[124];
  }
  let t65;
  if ($[125] !== t49 || $[126] !== t54 || $[127] !== t59 || $[128] !== t64) {
    t65 = [t49, t54, t59, t64];
    $[125] = t49;
    $[126] = t54;
    $[127] = t59;
    $[128] = t64;
    $[129] = t65;
  } else {
    t65 = $[129];
  }
  let t66;
  if ($[130] !== t44 || $[131] !== t65) {
    t66 = {
      id: "implementation",
      name: t44,
      subsection: t65
    };
    $[130] = t44;
    $[131] = t65;
    $[132] = t66;
  } else {
    t66 = $[132];
  }
  let t67;
  if ($[133] !== t) {
    t67 = t("products.aboutUs");
    $[133] = t;
    $[134] = t67;
  } else {
    t67 = $[134];
  }
  let t68;
  if ($[135] !== t) {
    t68 = t("products.aboutCompany.description");
    $[135] = t;
    $[136] = t68;
  } else {
    t68 = $[136];
  }
  let t69;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <UserGroupIcon />;
    $[137] = t69;
  } else {
    t69 = $[137];
  }
  let t70;
  if ($[138] !== t) {
    t70 = t("products.aboutCompany");
    $[138] = t;
    $[139] = t70;
  } else {
    t70 = $[139];
  }
  let t71;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = LINKS();
    $[140] = t71;
  } else {
    t71 = $[140];
  }
  let t72;
  if ($[141] !== t68 || $[142] !== t70) {
    t72 = {
      id: "aboutCompany",
      description: t68,
      icon: t69,
      name: t70,
      path: t71.aboutCompany
    };
    $[141] = t68;
    $[142] = t70;
    $[143] = t72;
  } else {
    t72 = $[143];
  }
  let t73;
  if ($[144] !== t) {
    t73 = t("products.forMedia.description");
    $[144] = t;
    $[145] = t73;
  } else {
    t73 = $[145];
  }
  let t74;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <BookOpenIcon />;
    $[146] = t74;
  } else {
    t74 = $[146];
  }
  let t75;
  if ($[147] !== t) {
    t75 = t("products.forMedia");
    $[147] = t;
    $[148] = t75;
  } else {
    t75 = $[148];
  }
  let t76;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = LINKS();
    $[149] = t76;
  } else {
    t76 = $[149];
  }
  let t77;
  if ($[150] !== t73 || $[151] !== t75) {
    t77 = {
      id: "forMedia",
      description: t73,
      icon: t74,
      name: t75,
      path: t76.forMedia
    };
    $[150] = t73;
    $[151] = t75;
    $[152] = t77;
  } else {
    t77 = $[152];
  }
  let t78;
  if ($[153] !== t) {
    t78 = t("products.career.description");
    $[153] = t;
    $[154] = t78;
  } else {
    t78 = $[154];
  }
  let t79;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <BriefcaseIcon />;
    $[155] = t79;
  } else {
    t79 = $[155];
  }
  let t80;
  if ($[156] !== t) {
    t80 = t("products.career");
    $[156] = t;
    $[157] = t80;
  } else {
    t80 = $[157];
  }
  let t81;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = LINKS();
    $[158] = t81;
  } else {
    t81 = $[158];
  }
  let t82;
  if ($[159] !== t78 || $[160] !== t80) {
    t82 = {
      id: "career",
      description: t78,
      icon: t79,
      name: t80,
      path: t81.career
    };
    $[159] = t78;
    $[160] = t80;
    $[161] = t82;
  } else {
    t82 = $[161];
  }
  let t83;
  if ($[162] !== t) {
    t83 = t("products.blog.description");
    $[162] = t;
    $[163] = t83;
  } else {
    t83 = $[163];
  }
  let t84;
  if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <NewspaperIcon />;
    $[164] = t84;
  } else {
    t84 = $[164];
  }
  let t85;
  if ($[165] !== t) {
    t85 = t("products.blog");
    $[165] = t;
    $[166] = t85;
  } else {
    t85 = $[166];
  }
  let t86;
  if ($[167] === Symbol.for("react.memo_cache_sentinel")) {
    t86 = EXTERNAL_LINKS();
    $[167] = t86;
  } else {
    t86 = $[167];
  }
  let t87;
  if ($[168] !== t83 || $[169] !== t85) {
    t87 = {
      id: "blog",
      description: t83,
      icon: t84,
      name: t85,
      path: t86.blog
    };
    $[168] = t83;
    $[169] = t85;
    $[170] = t87;
  } else {
    t87 = $[170];
  }
  let t88;
  if ($[171] !== t72 || $[172] !== t77 || $[173] !== t82 || $[174] !== t87) {
    t88 = [t72, t77, t82, t87];
    $[171] = t72;
    $[172] = t77;
    $[173] = t82;
    $[174] = t87;
    $[175] = t88;
  } else {
    t88 = $[175];
  }
  let t89;
  if ($[176] !== t67 || $[177] !== t88) {
    t89 = {
      id: "aboutUs",
      name: t67,
      subsection: t88
    };
    $[176] = t67;
    $[177] = t88;
    $[178] = t89;
  } else {
    t89 = $[178];
  }
  let t90;
  if ($[179] !== t) {
    t90 = t("products.other");
    $[179] = t;
    $[180] = t90;
  } else {
    t90 = $[180];
  }
  let t91;
  if ($[181] !== t) {
    t91 = t("products.faq.description");
    $[181] = t;
    $[182] = t91;
  } else {
    t91 = $[182];
  }
  let t92;
  if ($[183] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = <ChatIcon />;
    $[183] = t92;
  } else {
    t92 = $[183];
  }
  let t93;
  if ($[184] !== t) {
    t93 = t("products.faq");
    $[184] = t;
    $[185] = t93;
  } else {
    t93 = $[185];
  }
  let t94;
  if ($[186] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = LINKS();
    $[186] = t94;
  } else {
    t94 = $[186];
  }
  let t95;
  if ($[187] !== t91 || $[188] !== t93) {
    t95 = {
      id: "faq",
      description: t91,
      icon: t92,
      name: t93,
      path: t94.faq
    };
    $[187] = t91;
    $[188] = t93;
    $[189] = t95;
  } else {
    t95 = $[189];
  }
  let t96;
  if ($[190] !== t) {
    t96 = t("products.pricing.description");
    $[190] = t;
    $[191] = t96;
  } else {
    t96 = $[191];
  }
  let t97;
  if ($[192] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = <PayIcon />;
    $[192] = t97;
  } else {
    t97 = $[192];
  }
  let t98;
  if ($[193] !== t) {
    t98 = t("products.pricing");
    $[193] = t;
    $[194] = t98;
  } else {
    t98 = $[194];
  }
  let t99;
  if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
    t99 = LINKS();
    $[195] = t99;
  } else {
    t99 = $[195];
  }
  let t100;
  if ($[196] !== t96 || $[197] !== t98) {
    t100 = {
      id: "pricing",
      description: t96,
      icon: t97,
      name: t98,
      path: t99.pricing
    };
    $[196] = t96;
    $[197] = t98;
    $[198] = t100;
  } else {
    t100 = $[198];
  }
  let t101;
  if ($[199] !== t) {
    t101 = t("products.contact.description");
    $[199] = t;
    $[200] = t101;
  } else {
    t101 = $[200];
  }
  let t102;
  if ($[201] === Symbol.for("react.memo_cache_sentinel")) {
    t102 = <PhoneIcon />;
    $[201] = t102;
  } else {
    t102 = $[201];
  }
  let t103;
  if ($[202] !== t) {
    t103 = t("products.contact");
    $[202] = t;
    $[203] = t103;
  } else {
    t103 = $[203];
  }
  let t104;
  if ($[204] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = LINKS();
    $[204] = t104;
  } else {
    t104 = $[204];
  }
  let t105;
  if ($[205] !== t101 || $[206] !== t103) {
    t105 = {
      id: "contact",
      description: t101,
      icon: t102,
      name: t103,
      path: t104.contact
    };
    $[205] = t101;
    $[206] = t103;
    $[207] = t105;
  } else {
    t105 = $[207];
  }
  let t106;
  if ($[208] !== t) {
    t106 = t("products.termsAndConditions.description");
    $[208] = t;
    $[209] = t106;
  } else {
    t106 = $[209];
  }
  let t107;
  if ($[210] === Symbol.for("react.memo_cache_sentinel")) {
    t107 = <ClipboardIcon />;
    $[210] = t107;
  } else {
    t107 = $[210];
  }
  let t108;
  if ($[211] !== t) {
    t108 = t("products.termsAndConditions");
    $[211] = t;
    $[212] = t108;
  } else {
    t108 = $[212];
  }
  let t109;
  if ($[213] === Symbol.for("react.memo_cache_sentinel")) {
    t109 = LINKS();
    $[213] = t109;
  } else {
    t109 = $[213];
  }
  let t110;
  if ($[214] !== t106 || $[215] !== t108) {
    t110 = {
      id: "termsAndConditions",
      description: t106,
      icon: t107,
      name: t108,
      path: t109.termsAndConditions
    };
    $[214] = t106;
    $[215] = t108;
    $[216] = t110;
  } else {
    t110 = $[216];
  }
  let t111;
  if ($[217] !== t100 || $[218] !== t105 || $[219] !== t110 || $[220] !== t95) {
    t111 = [t95, t100, t105, t110];
    $[217] = t100;
    $[218] = t105;
    $[219] = t110;
    $[220] = t95;
    $[221] = t111;
  } else {
    t111 = $[221];
  }
  let t112;
  if ($[222] !== t111 || $[223] !== t90) {
    t112 = {
      id: "other",
      name: t90,
      subsection: t111
    };
    $[222] = t111;
    $[223] = t90;
    $[224] = t112;
  } else {
    t112 = $[224];
  }
  let t113;
  if ($[225] !== t112 || $[226] !== t27 || $[227] !== t30 || $[228] !== t43 || $[229] !== t66 || $[230] !== t89) {
    t113 = [t27, t30, t43, t66, t89, t112];
    $[225] = t112;
    $[226] = t27;
    $[227] = t30;
    $[228] = t43;
    $[229] = t66;
    $[230] = t89;
    $[231] = t113;
  } else {
    t113 = $[231];
  }
  const sectionsCs = t113 satisfies Section[];
  let t114;
  if ($[232] !== t) {
    t114 = t("products");
    $[232] = t;
    $[233] = t114;
  } else {
    t114 = $[233];
  }
  let t115;
  if ($[234] !== t) {
    t115 = t("products.payInThree.description");
    $[234] = t;
    $[235] = t115;
  } else {
    t115 = $[235];
  }
  let t116;
  if ($[236] === Symbol.for("react.memo_cache_sentinel")) {
    t116 = <ThirdIcon />;
    $[236] = t116;
  } else {
    t116 = $[236];
  }
  let t117;
  if ($[237] !== t) {
    t117 = t("products.payInThree");
    $[237] = t;
    $[238] = t117;
  } else {
    t117 = $[238];
  }
  let t118;
  if ($[239] === Symbol.for("react.memo_cache_sentinel")) {
    t118 = LINKS();
    $[239] = t118;
  } else {
    t118 = $[239];
  }
  let t119;
  if ($[240] !== t115 || $[241] !== t117) {
    t119 = {
      id: "payInThree",
      description: t115,
      icon: t116,
      name: t117,
      path: t118.payInThree
    };
    $[240] = t115;
    $[241] = t117;
    $[242] = t119;
  } else {
    t119 = $[242];
  }
  let t120;
  if ($[243] !== t) {
    t120 = t("products.mobileApp.description");
    $[243] = t;
    $[244] = t120;
  } else {
    t120 = $[244];
  }
  let t121;
  if ($[245] === Symbol.for("react.memo_cache_sentinel")) {
    t121 = <MobileAppIcon />;
    $[245] = t121;
  } else {
    t121 = $[245];
  }
  let t122;
  if ($[246] !== t) {
    t122 = t("products.mobileApp");
    $[246] = t;
    $[247] = t122;
  } else {
    t122 = $[247];
  }
  let t123;
  if ($[248] === Symbol.for("react.memo_cache_sentinel")) {
    t123 = LINKS();
    $[248] = t123;
  } else {
    t123 = $[248];
  }
  let t124;
  if ($[249] !== t120 || $[250] !== t122) {
    t124 = {
      id: "mobileApp",
      description: t120,
      icon: t121,
      name: t122,
      path: t123.mobileApp
    };
    $[249] = t120;
    $[250] = t122;
    $[251] = t124;
  } else {
    t124 = $[251];
  }
  let t125;
  if ($[252] !== t119 || $[253] !== t124) {
    t125 = [t119, t124];
    $[252] = t119;
    $[253] = t124;
    $[254] = t125;
  } else {
    t125 = $[254];
  }
  let t126;
  if ($[255] !== t114 || $[256] !== t125) {
    t126 = {
      id: "products",
      name: t114,
      subsection: t125
    };
    $[255] = t114;
    $[256] = t125;
    $[257] = t126;
  } else {
    t126 = $[257];
  }
  let t127;
  if ($[258] !== t) {
    t127 = t("products.whereToShop");
    $[258] = t;
    $[259] = t127;
  } else {
    t127 = $[259];
  }
  let t128;
  if ($[260] === Symbol.for("react.memo_cache_sentinel")) {
    t128 = LINKS();
    $[260] = t128;
  } else {
    t128 = $[260];
  }
  let t129;
  if ($[261] !== t127) {
    t129 = {
      id: "whereToShop",
      name: t127,
      path: t128.whereToShop
    };
    $[261] = t127;
    $[262] = t129;
  } else {
    t129 = $[262];
  }
  let t130;
  if ($[263] !== t) {
    t130 = t("products.implementation");
    $[263] = t;
    $[264] = t130;
  } else {
    t130 = $[264];
  }
  let t131;
  if ($[265] !== t) {
    t131 = t("products.forEshops.description");
    $[265] = t;
    $[266] = t131;
  } else {
    t131 = $[266];
  }
  let t132;
  if ($[267] === Symbol.for("react.memo_cache_sentinel")) {
    t132 = <ShoppingCartIcon />;
    $[267] = t132;
  } else {
    t132 = $[267];
  }
  let t133;
  if ($[268] !== t) {
    t133 = t("products.forEshops");
    $[268] = t;
    $[269] = t133;
  } else {
    t133 = $[269];
  }
  let t134;
  if ($[270] === Symbol.for("react.memo_cache_sentinel")) {
    t134 = LINKS();
    $[270] = t134;
  } else {
    t134 = $[270];
  }
  let t135;
  if ($[271] !== t131 || $[272] !== t133) {
    t135 = {
      id: "forEshops",
      description: t131,
      icon: t132,
      name: t133,
      path: t134.forEshops
    };
    $[271] = t131;
    $[272] = t133;
    $[273] = t135;
  } else {
    t135 = $[273];
  }
  let t136;
  if ($[274] !== t) {
    t136 = t("products.forDevelopers.description");
    $[274] = t;
    $[275] = t136;
  } else {
    t136 = $[275];
  }
  let t137;
  if ($[276] === Symbol.for("react.memo_cache_sentinel")) {
    t137 = <DesktopComputerIcon />;
    $[276] = t137;
  } else {
    t137 = $[276];
  }
  let t138;
  if ($[277] !== t) {
    t138 = t("products.forDevelopers");
    $[277] = t;
    $[278] = t138;
  } else {
    t138 = $[278];
  }
  let t139;
  if ($[279] === Symbol.for("react.memo_cache_sentinel")) {
    t139 = LINKS();
    $[279] = t139;
  } else {
    t139 = $[279];
  }
  let t140;
  if ($[280] !== t136 || $[281] !== t138) {
    t140 = {
      id: "forDevelopers",
      description: t136,
      icon: t137,
      name: t138,
      path: t139.forDevelopers
    };
    $[280] = t136;
    $[281] = t138;
    $[282] = t140;
  } else {
    t140 = $[282];
  }
  let t141;
  if ($[283] !== t) {
    t141 = t("products.checkoutManual.description");
    $[283] = t;
    $[284] = t141;
  } else {
    t141 = $[284];
  }
  let t142;
  if ($[285] === Symbol.for("react.memo_cache_sentinel")) {
    t142 = <TemplateIcon />;
    $[285] = t142;
  } else {
    t142 = $[285];
  }
  let t143;
  if ($[286] !== t) {
    t143 = t("products.checkoutManual");
    $[286] = t;
    $[287] = t143;
  } else {
    t143 = $[287];
  }
  let t144;
  if ($[288] === Symbol.for("react.memo_cache_sentinel")) {
    t144 = EXTERNAL_LINKS();
    $[288] = t144;
  } else {
    t144 = $[288];
  }
  let t145;
  if ($[289] !== t141 || $[290] !== t143) {
    t145 = {
      id: "checkoutManual",
      description: t141,
      icon: t142,
      name: t143,
      path: t144.checkoutManualSk
    };
    $[289] = t141;
    $[290] = t143;
    $[291] = t145;
  } else {
    t145 = $[291];
  }
  let t146;
  if ($[292] !== t135 || $[293] !== t140 || $[294] !== t145) {
    t146 = [t135, t140, t145];
    $[292] = t135;
    $[293] = t140;
    $[294] = t145;
    $[295] = t146;
  } else {
    t146 = $[295];
  }
  let t147;
  if ($[296] !== t130 || $[297] !== t146) {
    t147 = {
      id: "implementation",
      name: t130,
      subsection: t146
    };
    $[296] = t130;
    $[297] = t146;
    $[298] = t147;
  } else {
    t147 = $[298];
  }
  let t148;
  if ($[299] !== t) {
    t148 = t("products.aboutUs");
    $[299] = t;
    $[300] = t148;
  } else {
    t148 = $[300];
  }
  let t149;
  if ($[301] !== t) {
    t149 = t("products.aboutCompany.description");
    $[301] = t;
    $[302] = t149;
  } else {
    t149 = $[302];
  }
  let t150;
  if ($[303] === Symbol.for("react.memo_cache_sentinel")) {
    t150 = <UserGroupIcon />;
    $[303] = t150;
  } else {
    t150 = $[303];
  }
  let t151;
  if ($[304] !== t) {
    t151 = t("products.aboutCompany");
    $[304] = t;
    $[305] = t151;
  } else {
    t151 = $[305];
  }
  let t152;
  if ($[306] === Symbol.for("react.memo_cache_sentinel")) {
    t152 = LINKS();
    $[306] = t152;
  } else {
    t152 = $[306];
  }
  let t153;
  if ($[307] !== t149 || $[308] !== t151) {
    t153 = {
      id: "aboutCompany",
      description: t149,
      icon: t150,
      name: t151,
      path: t152.aboutCompany
    };
    $[307] = t149;
    $[308] = t151;
    $[309] = t153;
  } else {
    t153 = $[309];
  }
  let t154;
  if ($[310] !== t) {
    t154 = t("products.forMedia.description");
    $[310] = t;
    $[311] = t154;
  } else {
    t154 = $[311];
  }
  let t155;
  if ($[312] === Symbol.for("react.memo_cache_sentinel")) {
    t155 = <BookOpenIcon />;
    $[312] = t155;
  } else {
    t155 = $[312];
  }
  let t156;
  if ($[313] !== t) {
    t156 = t("products.forMedia");
    $[313] = t;
    $[314] = t156;
  } else {
    t156 = $[314];
  }
  let t157;
  if ($[315] === Symbol.for("react.memo_cache_sentinel")) {
    t157 = LINKS();
    $[315] = t157;
  } else {
    t157 = $[315];
  }
  let t158;
  if ($[316] !== t154 || $[317] !== t156) {
    t158 = {
      id: "forMedia",
      description: t154,
      icon: t155,
      name: t156,
      path: t157.forMedia
    };
    $[316] = t154;
    $[317] = t156;
    $[318] = t158;
  } else {
    t158 = $[318];
  }
  let t159;
  if ($[319] !== t153 || $[320] !== t158) {
    t159 = [t153, t158];
    $[319] = t153;
    $[320] = t158;
    $[321] = t159;
  } else {
    t159 = $[321];
  }
  let t160;
  if ($[322] !== t148 || $[323] !== t159) {
    t160 = {
      id: "aboutUs",
      name: t148,
      subsection: t159
    };
    $[322] = t148;
    $[323] = t159;
    $[324] = t160;
  } else {
    t160 = $[324];
  }
  let t161;
  if ($[325] !== t) {
    t161 = t("products.other");
    $[325] = t;
    $[326] = t161;
  } else {
    t161 = $[326];
  }
  let t162;
  if ($[327] !== t) {
    t162 = t("products.faq.description");
    $[327] = t;
    $[328] = t162;
  } else {
    t162 = $[328];
  }
  let t163;
  if ($[329] === Symbol.for("react.memo_cache_sentinel")) {
    t163 = <ChatIcon />;
    $[329] = t163;
  } else {
    t163 = $[329];
  }
  let t164;
  if ($[330] !== t) {
    t164 = t("products.faq");
    $[330] = t;
    $[331] = t164;
  } else {
    t164 = $[331];
  }
  let t165;
  if ($[332] === Symbol.for("react.memo_cache_sentinel")) {
    t165 = LINKS();
    $[332] = t165;
  } else {
    t165 = $[332];
  }
  let t166;
  if ($[333] !== t162 || $[334] !== t164) {
    t166 = {
      id: "faq",
      description: t162,
      icon: t163,
      name: t164,
      path: t165.faq
    };
    $[333] = t162;
    $[334] = t164;
    $[335] = t166;
  } else {
    t166 = $[335];
  }
  let t167;
  if ($[336] !== t) {
    t167 = t("products.pricing.description");
    $[336] = t;
    $[337] = t167;
  } else {
    t167 = $[337];
  }
  let t168;
  if ($[338] === Symbol.for("react.memo_cache_sentinel")) {
    t168 = <PayIcon />;
    $[338] = t168;
  } else {
    t168 = $[338];
  }
  let t169;
  if ($[339] !== t) {
    t169 = t("products.pricing");
    $[339] = t;
    $[340] = t169;
  } else {
    t169 = $[340];
  }
  let t170;
  if ($[341] === Symbol.for("react.memo_cache_sentinel")) {
    t170 = LINKS();
    $[341] = t170;
  } else {
    t170 = $[341];
  }
  let t171;
  if ($[342] !== t167 || $[343] !== t169) {
    t171 = {
      id: "pricing",
      description: t167,
      icon: t168,
      name: t169,
      path: t170.pricing
    };
    $[342] = t167;
    $[343] = t169;
    $[344] = t171;
  } else {
    t171 = $[344];
  }
  let t172;
  if ($[345] !== t) {
    t172 = t("products.contact.description");
    $[345] = t;
    $[346] = t172;
  } else {
    t172 = $[346];
  }
  let t173;
  if ($[347] === Symbol.for("react.memo_cache_sentinel")) {
    t173 = <PhoneIcon />;
    $[347] = t173;
  } else {
    t173 = $[347];
  }
  let t174;
  if ($[348] !== t) {
    t174 = t("products.contact");
    $[348] = t;
    $[349] = t174;
  } else {
    t174 = $[349];
  }
  let t175;
  if ($[350] === Symbol.for("react.memo_cache_sentinel")) {
    t175 = LINKS();
    $[350] = t175;
  } else {
    t175 = $[350];
  }
  let t176;
  if ($[351] !== t172 || $[352] !== t174) {
    t176 = {
      id: "contact",
      description: t172,
      icon: t173,
      name: t174,
      path: t175.contact
    };
    $[351] = t172;
    $[352] = t174;
    $[353] = t176;
  } else {
    t176 = $[353];
  }
  let t177;
  if ($[354] !== t) {
    t177 = t("products.termsAndConditions.description");
    $[354] = t;
    $[355] = t177;
  } else {
    t177 = $[355];
  }
  let t178;
  if ($[356] === Symbol.for("react.memo_cache_sentinel")) {
    t178 = <ClipboardIcon />;
    $[356] = t178;
  } else {
    t178 = $[356];
  }
  let t179;
  if ($[357] !== t) {
    t179 = t("products.termsAndConditions");
    $[357] = t;
    $[358] = t179;
  } else {
    t179 = $[358];
  }
  let t180;
  if ($[359] === Symbol.for("react.memo_cache_sentinel")) {
    t180 = LINKS();
    $[359] = t180;
  } else {
    t180 = $[359];
  }
  let t181;
  if ($[360] !== t177 || $[361] !== t179) {
    t181 = {
      id: "termsAndConditions",
      description: t177,
      icon: t178,
      name: t179,
      path: t180.termsAndConditions
    };
    $[360] = t177;
    $[361] = t179;
    $[362] = t181;
  } else {
    t181 = $[362];
  }
  let t182;
  if ($[363] !== t166 || $[364] !== t171 || $[365] !== t176 || $[366] !== t181) {
    t182 = [t166, t171, t176, t181];
    $[363] = t166;
    $[364] = t171;
    $[365] = t176;
    $[366] = t181;
    $[367] = t182;
  } else {
    t182 = $[367];
  }
  let t183;
  if ($[368] !== t161 || $[369] !== t182) {
    t183 = {
      id: "other",
      name: t161,
      promo: undefined,
      subsection: t182
    };
    $[368] = t161;
    $[369] = t182;
    $[370] = t183;
  } else {
    t183 = $[370];
  }
  let t184;
  if ($[371] !== t126 || $[372] !== t129 || $[373] !== t147 || $[374] !== t160 || $[375] !== t183) {
    t184 = [t126, t129, t147, t160, t183];
    $[371] = t126;
    $[372] = t129;
    $[373] = t147;
    $[374] = t160;
    $[375] = t183;
    $[376] = t184;
  } else {
    t184 = $[376];
  }
  const sectionsSk = t184 satisfies Section[];
  let t185;
  if ($[377] !== t) {
    t185 = t("products");
    $[377] = t;
    $[378] = t185;
  } else {
    t185 = $[378];
  }
  let t186;
  if ($[379] !== t) {
    t186 = t("products.payInThree.description");
    $[379] = t;
    $[380] = t186;
  } else {
    t186 = $[380];
  }
  let t187;
  if ($[381] === Symbol.for("react.memo_cache_sentinel")) {
    t187 = <ThirdIcon />;
    $[381] = t187;
  } else {
    t187 = $[381];
  }
  let t188;
  if ($[382] !== t) {
    t188 = t("products.payInThree");
    $[382] = t;
    $[383] = t188;
  } else {
    t188 = $[383];
  }
  let t189;
  if ($[384] === Symbol.for("react.memo_cache_sentinel")) {
    t189 = LINKS();
    $[384] = t189;
  } else {
    t189 = $[384];
  }
  let t190;
  if ($[385] !== t186 || $[386] !== t188) {
    t190 = {
      id: "payInThree",
      description: t186,
      icon: t187,
      name: t188,
      path: t189.payInThree
    };
    $[385] = t186;
    $[386] = t188;
    $[387] = t190;
  } else {
    t190 = $[387];
  }
  let t191;
  if ($[388] !== t) {
    t191 = t("products.mobileApp.description");
    $[388] = t;
    $[389] = t191;
  } else {
    t191 = $[389];
  }
  let t192;
  if ($[390] === Symbol.for("react.memo_cache_sentinel")) {
    t192 = <MobileAppIcon />;
    $[390] = t192;
  } else {
    t192 = $[390];
  }
  let t193;
  if ($[391] !== t) {
    t193 = t("products.mobileApp");
    $[391] = t;
    $[392] = t193;
  } else {
    t193 = $[392];
  }
  let t194;
  if ($[393] === Symbol.for("react.memo_cache_sentinel")) {
    t194 = LINKS();
    $[393] = t194;
  } else {
    t194 = $[393];
  }
  let t195;
  if ($[394] !== t191 || $[395] !== t193) {
    t195 = {
      id: "mobileApp",
      description: t191,
      icon: t192,
      name: t193,
      path: t194.mobileApp
    };
    $[394] = t191;
    $[395] = t193;
    $[396] = t195;
  } else {
    t195 = $[396];
  }
  let t196;
  if ($[397] !== t190 || $[398] !== t195) {
    t196 = [t190, t195];
    $[397] = t190;
    $[398] = t195;
    $[399] = t196;
  } else {
    t196 = $[399];
  }
  let t197;
  if ($[400] !== t185 || $[401] !== t196) {
    t197 = {
      id: "products",
      name: t185,
      subsection: t196
    };
    $[400] = t185;
    $[401] = t196;
    $[402] = t197;
  } else {
    t197 = $[402];
  }
  let t198;
  if ($[403] !== t) {
    t198 = t("products.whereToShop");
    $[403] = t;
    $[404] = t198;
  } else {
    t198 = $[404];
  }
  let t199;
  if ($[405] === Symbol.for("react.memo_cache_sentinel")) {
    t199 = LINKS();
    $[405] = t199;
  } else {
    t199 = $[405];
  }
  let t200;
  if ($[406] !== t198) {
    t200 = {
      id: "whereToShop",
      name: t198,
      path: t199.whereToShop
    };
    $[406] = t198;
    $[407] = t200;
  } else {
    t200 = $[407];
  }
  let t201;
  if ($[408] !== t) {
    t201 = t("products.implementation");
    $[408] = t;
    $[409] = t201;
  } else {
    t201 = $[409];
  }
  let t202;
  if ($[410] !== t) {
    t202 = t("products.forEshops.description");
    $[410] = t;
    $[411] = t202;
  } else {
    t202 = $[411];
  }
  let t203;
  if ($[412] === Symbol.for("react.memo_cache_sentinel")) {
    t203 = <ShoppingCartIcon />;
    $[412] = t203;
  } else {
    t203 = $[412];
  }
  let t204;
  if ($[413] !== t) {
    t204 = t("products.forEshops");
    $[413] = t;
    $[414] = t204;
  } else {
    t204 = $[414];
  }
  let t205;
  if ($[415] === Symbol.for("react.memo_cache_sentinel")) {
    t205 = LINKS();
    $[415] = t205;
  } else {
    t205 = $[415];
  }
  let t206;
  if ($[416] !== t202 || $[417] !== t204) {
    t206 = {
      id: "forEshops",
      description: t202,
      icon: t203,
      name: t204,
      path: t205.forEshops
    };
    $[416] = t202;
    $[417] = t204;
    $[418] = t206;
  } else {
    t206 = $[418];
  }
  let t207;
  if ($[419] !== t) {
    t207 = t("products.forDevelopers.description");
    $[419] = t;
    $[420] = t207;
  } else {
    t207 = $[420];
  }
  let t208;
  if ($[421] === Symbol.for("react.memo_cache_sentinel")) {
    t208 = <DesktopComputerIcon />;
    $[421] = t208;
  } else {
    t208 = $[421];
  }
  let t209;
  if ($[422] !== t) {
    t209 = t("products.forDevelopers");
    $[422] = t;
    $[423] = t209;
  } else {
    t209 = $[423];
  }
  let t210;
  if ($[424] === Symbol.for("react.memo_cache_sentinel")) {
    t210 = LINKS();
    $[424] = t210;
  } else {
    t210 = $[424];
  }
  let t211;
  if ($[425] !== t207 || $[426] !== t209) {
    t211 = {
      id: "forDevelopers",
      description: t207,
      icon: t208,
      name: t209,
      path: t210.forDevelopers
    };
    $[425] = t207;
    $[426] = t209;
    $[427] = t211;
  } else {
    t211 = $[427];
  }
  let t212;
  if ($[428] !== t) {
    t212 = t("products.checkoutManual.description");
    $[428] = t;
    $[429] = t212;
  } else {
    t212 = $[429];
  }
  let t213;
  if ($[430] === Symbol.for("react.memo_cache_sentinel")) {
    t213 = <TemplateIcon />;
    $[430] = t213;
  } else {
    t213 = $[430];
  }
  let t214;
  if ($[431] !== t) {
    t214 = t("products.checkoutManual");
    $[431] = t;
    $[432] = t214;
  } else {
    t214 = $[432];
  }
  let t215;
  if ($[433] === Symbol.for("react.memo_cache_sentinel")) {
    t215 = EXTERNAL_LINKS();
    $[433] = t215;
  } else {
    t215 = $[433];
  }
  let t216;
  if ($[434] !== t212 || $[435] !== t214) {
    t216 = {
      id: "checkoutManual",
      description: t212,
      icon: t213,
      name: t214,
      path: t215.checkoutManualHu
    };
    $[434] = t212;
    $[435] = t214;
    $[436] = t216;
  } else {
    t216 = $[436];
  }
  let t217;
  if ($[437] !== t206 || $[438] !== t211 || $[439] !== t216) {
    t217 = [t206, t211, t216];
    $[437] = t206;
    $[438] = t211;
    $[439] = t216;
    $[440] = t217;
  } else {
    t217 = $[440];
  }
  let t218;
  if ($[441] !== t201 || $[442] !== t217) {
    t218 = {
      id: "implementation",
      name: t201,
      subsection: t217
    };
    $[441] = t201;
    $[442] = t217;
    $[443] = t218;
  } else {
    t218 = $[443];
  }
  let t219;
  if ($[444] !== t) {
    t219 = t("products.aboutUs");
    $[444] = t;
    $[445] = t219;
  } else {
    t219 = $[445];
  }
  let t220;
  if ($[446] !== t) {
    t220 = t("products.aboutCompany.description");
    $[446] = t;
    $[447] = t220;
  } else {
    t220 = $[447];
  }
  let t221;
  if ($[448] === Symbol.for("react.memo_cache_sentinel")) {
    t221 = <UserGroupIcon />;
    $[448] = t221;
  } else {
    t221 = $[448];
  }
  let t222;
  if ($[449] !== t) {
    t222 = t("products.aboutCompany");
    $[449] = t;
    $[450] = t222;
  } else {
    t222 = $[450];
  }
  let t223;
  if ($[451] === Symbol.for("react.memo_cache_sentinel")) {
    t223 = LINKS();
    $[451] = t223;
  } else {
    t223 = $[451];
  }
  let t224;
  if ($[452] !== t220 || $[453] !== t222) {
    t224 = {
      id: "aboutCompany",
      description: t220,
      icon: t221,
      name: t222,
      path: t223.aboutCompany
    };
    $[452] = t220;
    $[453] = t222;
    $[454] = t224;
  } else {
    t224 = $[454];
  }
  let t225;
  if ($[455] !== t) {
    t225 = t("products.forMedia.description");
    $[455] = t;
    $[456] = t225;
  } else {
    t225 = $[456];
  }
  let t226;
  if ($[457] === Symbol.for("react.memo_cache_sentinel")) {
    t226 = <BookOpenIcon />;
    $[457] = t226;
  } else {
    t226 = $[457];
  }
  let t227;
  if ($[458] !== t) {
    t227 = t("products.forMedia");
    $[458] = t;
    $[459] = t227;
  } else {
    t227 = $[459];
  }
  let t228;
  if ($[460] === Symbol.for("react.memo_cache_sentinel")) {
    t228 = LINKS();
    $[460] = t228;
  } else {
    t228 = $[460];
  }
  let t229;
  if ($[461] !== t225 || $[462] !== t227) {
    t229 = {
      id: "forMedia",
      description: t225,
      icon: t226,
      name: t227,
      path: t228.forMedia
    };
    $[461] = t225;
    $[462] = t227;
    $[463] = t229;
  } else {
    t229 = $[463];
  }
  let t230;
  if ($[464] !== t224 || $[465] !== t229) {
    t230 = [t224, t229];
    $[464] = t224;
    $[465] = t229;
    $[466] = t230;
  } else {
    t230 = $[466];
  }
  let t231;
  if ($[467] !== t219 || $[468] !== t230) {
    t231 = {
      id: "aboutUs",
      name: t219,
      subsection: t230
    };
    $[467] = t219;
    $[468] = t230;
    $[469] = t231;
  } else {
    t231 = $[469];
  }
  let t232;
  if ($[470] !== t) {
    t232 = t("products.other");
    $[470] = t;
    $[471] = t232;
  } else {
    t232 = $[471];
  }
  let t233;
  if ($[472] !== t) {
    t233 = t("products.faq.description");
    $[472] = t;
    $[473] = t233;
  } else {
    t233 = $[473];
  }
  let t234;
  if ($[474] === Symbol.for("react.memo_cache_sentinel")) {
    t234 = <ChatIcon />;
    $[474] = t234;
  } else {
    t234 = $[474];
  }
  let t235;
  if ($[475] !== t) {
    t235 = t("products.faq");
    $[475] = t;
    $[476] = t235;
  } else {
    t235 = $[476];
  }
  let t236;
  if ($[477] === Symbol.for("react.memo_cache_sentinel")) {
    t236 = LINKS();
    $[477] = t236;
  } else {
    t236 = $[477];
  }
  let t237;
  if ($[478] !== t233 || $[479] !== t235) {
    t237 = {
      id: "faq",
      description: t233,
      icon: t234,
      name: t235,
      path: t236.faq
    };
    $[478] = t233;
    $[479] = t235;
    $[480] = t237;
  } else {
    t237 = $[480];
  }
  let t238;
  if ($[481] !== t) {
    t238 = t("products.pricing.description");
    $[481] = t;
    $[482] = t238;
  } else {
    t238 = $[482];
  }
  let t239;
  if ($[483] === Symbol.for("react.memo_cache_sentinel")) {
    t239 = <PayIcon />;
    $[483] = t239;
  } else {
    t239 = $[483];
  }
  let t240;
  if ($[484] !== t) {
    t240 = t("products.pricing");
    $[484] = t;
    $[485] = t240;
  } else {
    t240 = $[485];
  }
  let t241;
  if ($[486] === Symbol.for("react.memo_cache_sentinel")) {
    t241 = LINKS();
    $[486] = t241;
  } else {
    t241 = $[486];
  }
  let t242;
  if ($[487] !== t238 || $[488] !== t240) {
    t242 = {
      id: "pricing",
      description: t238,
      icon: t239,
      name: t240,
      path: t241.pricing
    };
    $[487] = t238;
    $[488] = t240;
    $[489] = t242;
  } else {
    t242 = $[489];
  }
  let t243;
  if ($[490] !== t) {
    t243 = t("products.contact.description");
    $[490] = t;
    $[491] = t243;
  } else {
    t243 = $[491];
  }
  let t244;
  if ($[492] === Symbol.for("react.memo_cache_sentinel")) {
    t244 = <PhoneIcon />;
    $[492] = t244;
  } else {
    t244 = $[492];
  }
  let t245;
  if ($[493] !== t) {
    t245 = t("products.contact");
    $[493] = t;
    $[494] = t245;
  } else {
    t245 = $[494];
  }
  let t246;
  if ($[495] === Symbol.for("react.memo_cache_sentinel")) {
    t246 = LINKS();
    $[495] = t246;
  } else {
    t246 = $[495];
  }
  let t247;
  if ($[496] !== t243 || $[497] !== t245) {
    t247 = {
      id: "contact",
      description: t243,
      icon: t244,
      name: t245,
      path: t246.contact
    };
    $[496] = t243;
    $[497] = t245;
    $[498] = t247;
  } else {
    t247 = $[498];
  }
  let t248;
  if ($[499] !== t) {
    t248 = t("products.termsAndConditions.description");
    $[499] = t;
    $[500] = t248;
  } else {
    t248 = $[500];
  }
  let t249;
  if ($[501] === Symbol.for("react.memo_cache_sentinel")) {
    t249 = <ClipboardIcon />;
    $[501] = t249;
  } else {
    t249 = $[501];
  }
  let t250;
  if ($[502] !== t) {
    t250 = t("products.termsAndConditions");
    $[502] = t;
    $[503] = t250;
  } else {
    t250 = $[503];
  }
  let t251;
  if ($[504] === Symbol.for("react.memo_cache_sentinel")) {
    t251 = LINKS();
    $[504] = t251;
  } else {
    t251 = $[504];
  }
  let t252;
  if ($[505] !== t248 || $[506] !== t250) {
    t252 = {
      id: "termsAndConditions",
      description: t248,
      icon: t249,
      name: t250,
      path: t251.termsAndConditions
    };
    $[505] = t248;
    $[506] = t250;
    $[507] = t252;
  } else {
    t252 = $[507];
  }
  let t253;
  if ($[508] !== t237 || $[509] !== t242 || $[510] !== t247 || $[511] !== t252) {
    t253 = [t237, t242, t247, t252];
    $[508] = t237;
    $[509] = t242;
    $[510] = t247;
    $[511] = t252;
    $[512] = t253;
  } else {
    t253 = $[512];
  }
  let t254;
  if ($[513] !== t232 || $[514] !== t253) {
    t254 = {
      id: "other",
      name: t232,
      promo: undefined,
      subsection: t253
    };
    $[513] = t232;
    $[514] = t253;
    $[515] = t254;
  } else {
    t254 = $[515];
  }
  let t255;
  if ($[516] !== t197 || $[517] !== t200 || $[518] !== t218 || $[519] !== t231 || $[520] !== t254) {
    t255 = [t197, t200, t218, t231, t254];
    $[516] = t197;
    $[517] = t200;
    $[518] = t218;
    $[519] = t231;
    $[520] = t254;
    $[521] = t255;
  } else {
    t255 = $[521];
  }
  const sectionsHu = t255 satisfies Section[];
  return isCsLanguage ? sectionsCs : isHuLanguage ? sectionsHu : sectionsSk;
}